/**
 * 소셜 로그인 / 백엔드 충돌 시 사용자에게 보여 줄 메시지 (Firebase 콘솔의
 * "계정당 이메일 하나" 정책과 동일한 의미를 프론트에서도 일관되게 표시)
 */
export const SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE =
  "Only one social sign-in method is allowed per email address.";

export const BLOCKED_ACCOUNT_MESSAGE =
  "This account has been blocked. Please contact the administrator.";

export const USER_INFO_UNAVAILABLE_MESSAGE =
  "Unable to verify user information. Please contact the administrator.";

export const USER_INFO_CHECK_FAILED_MESSAGE =
  "Failed to verify user information. Please try again later.";

export const SERVICE_UNAVAILABLE_MESSAGE =
  "The server is temporarily unavailable (503). Please try again later.";

export const AUTHENTICATION_ERROR_MESSAGE =
  "An authentication error occurred. Please try again later.";

export const EMAIL_ALREADY_REGISTERED_MESSAGE =
  "This email address is already registered.";

export const INVALID_EMAIL_MESSAGE = "Enter a valid email address.";

export const INCORRECT_PASSWORD_MESSAGE =
  "The email or password is incorrect.";

export const PASSWORD_CONTAINS_SPACE_MESSAGE =
  "Password cannot contain spaces.";

export const PASSWORD_MINIMUM_LENGTH_MESSAGE =
  "Password must be at least 8 characters.";

export const PASSWORD_REQUIRED_COMBINATION_MESSAGE =
  "Password must include a lowercase letter, a number, and a special symbol.";

export const PASSWORD_CONTAINS_EMAIL_MESSAGE =
  "Password cannot contain your email ID.";

export const SOCIAL_SIGN_IN_REQUIRED_ERROR_CODE = "auth/social-sign-in-required";

/**
 * 표기 정본은 `sign-in`이다. 과거 표기를 쓰던 시절의 원문도 계속 인식해야
 * 하므로 서버·Firebase 원문 매칭은 두 표기를 모두 받는다.
 */
const SOCIAL_SIGN_IN_REQUIRED_MESSAGE_RE =
  /This account uses (Google|Microsoft|Apple) (?:sign-in|login)\. Please continue with \1\./i;

export function socialSignInRequiredMessage(providerId: string): string {
  const provider = {
    "google.com": "Google",
    "microsoft.com": "Microsoft",
    "apple.com": "Apple",
  }[providerId];
  if (!provider) {
    return "This account uses a social sign-in. Please continue with social sign-in.";
  }
  return `This account uses ${provider} sign-in. Please continue with ${provider}.`;
}

/**
 * 소셜 provider 안내는 비밀번호가 아니라 이메일 주소만으로 판정되므로
 * Password가 아닌 Email address 입력에 표시해야 한다. (`STUDIO-AUTH-003` §2)
 */
export function isSocialSignInRequiredError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  if (code === SOCIAL_SIGN_IN_REQUIRED_ERROR_CODE) return true;

  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message.includes(SOCIAL_SIGN_IN_REQUIRED_ERROR_CODE) ||
    SOCIAL_SIGN_IN_REQUIRED_MESSAGE_RE.test(message)
  );
}

/**
 * Firebase·API 원문에 동일 이메일·다른 제공자 관련 코드가 있으면 통일 문구로 치환
 */
export function mapSocialEmailConflictMessage(raw: string): string {
  const n = raw.toLowerCase();
  if (n.includes("auth/account-exists-with-different-credential")) {
    return SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE;
  }
  return raw;
}

/**
 * 로그인 페이지 토스트용 — 충돌을 먼저 처리한 뒤 제공자/팝업 일반 메시지
 */
export function mapLoginErrorMessage(authError: string): string {
  const normalizedError = authError.toLowerCase();
  if (normalizedError.includes("auth/invalid-email")) {
    return INVALID_EMAIL_MESSAGE;
  }
  if (
    normalizedError.includes("auth/email-already-in-use") ||
    normalizedError.includes(EMAIL_ALREADY_REGISTERED_MESSAGE.toLowerCase())
  ) return EMAIL_ALREADY_REGISTERED_MESSAGE;
  if (
    normalizedError.includes("auth/invalid-credential") ||
    normalizedError.includes("auth/user-not-found") ||
    normalizedError.includes("auth/wrong-password") ||
    normalizedError.includes(INCORRECT_PASSWORD_MESSAGE.toLowerCase())
  ) return INCORRECT_PASSWORD_MESSAGE;
  if (
    normalizedError.includes("auth/password-contains-space") ||
    normalizedError.includes(PASSWORD_CONTAINS_SPACE_MESSAGE.toLowerCase())
  ) return PASSWORD_CONTAINS_SPACE_MESSAGE;
  if (
    normalizedError.includes("auth/password-minimum-length") ||
    normalizedError.includes(PASSWORD_MINIMUM_LENGTH_MESSAGE.toLowerCase())
  ) return PASSWORD_MINIMUM_LENGTH_MESSAGE;
  if (
    normalizedError.includes("auth/password-missing-required-combination") ||
    normalizedError.includes(PASSWORD_REQUIRED_COMBINATION_MESSAGE.toLowerCase())
  ) return PASSWORD_REQUIRED_COMBINATION_MESSAGE;
  if (
    normalizedError.includes("auth/password-contains-email") ||
    normalizedError.includes(PASSWORD_CONTAINS_EMAIL_MESSAGE.toLowerCase())
  ) return PASSWORD_CONTAINS_EMAIL_MESSAGE;

  const socialSignInMatch = authError.match(SOCIAL_SIGN_IN_REQUIRED_MESSAGE_RE);
  if (socialSignInMatch) return socialSignInMatch[0];

  const conflictMapped = mapSocialEmailConflictMessage(authError);
  if (conflictMapped === SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE) {
    return SOCIAL_LOGIN_EMAIL_CONFLICT_MESSAGE;
  }

  if (normalizedError.includes("google")) {
    return "Google sign-in failed. Please try again.";
  }

  if (normalizedError.includes("apple")) {
    return "Apple sign-in failed. Please try again.";
  }

  if (normalizedError.includes("microsoft")) {
    return "Microsoft sign-in failed. Please try again.";
  }

  if (
    normalizedError.includes("email/password") ||
    normalizedError.includes("이메일/비밀번호")
  ) {
    return "Email/password sign-in failed. Please try again.";
  }

  if (normalizedError.includes("guest")) {
    return "Guest sign-in failed. Please try again.";
  }

  if (
    normalizedError.includes("popup") ||
    normalizedError.includes("cancelled") ||
    normalizedError.includes("closed")
  ) {
    return AUTHENTICATION_ERROR_MESSAGE;
  }

  return AUTHENTICATION_ERROR_MESSAGE;
}
